import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc } from 'firebase/firestore';
import Notification from './Notification';
import Spinner from './Spinner';
import ProductDetailModal from './ProductDetailModal';
import ProductForm from './ProductForm';
import { useEditMode } from '../contexts/EditModeContext';
import {
  getCatalogCategories,
  matchesCatalogCategory,
  normalizeCatalogProduct,
  shouldShowCatalogProduct
} from '../utils/productCatalog';

import './Products.css';

const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) {
    return '0,00';
  }
  return number
    .toFixed(2) // Mantiene dos decimales
    .replace('.', ',') // Reemplaza el punto decimal por una coma
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.'); // Agrega puntos como separadores de miles
};

const Products = ({ cart, setCart }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);
  const [notification, setNotification] = useState({ message: '', show: false });
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const navigate = useNavigate();

  const { isEditMode, isAdmin } = useEditMode();
  const canEditCatalog = isAdmin && isEditMode;

  const fetchProducts = useCallback(async () => {
    try {
      const productsCollection = await getDocs(collection(db, 'otherProducts'));
      const productsData = productsCollection.docs
        .map(doc => normalizeCatalogProduct(doc.data(), doc.id))
        .sort((a, b) => a.model.localeCompare(b.model));
      setProducts(productsData);

      setCategories(getCatalogCategories(productsData));
    } catch (error) {
      console.error("Error fetching products:", error);
      setNotification({ message: 'Error al cargar productos.', show: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const addToCart = (product, quantity) => {
    const existingProduct = cart.find(item => item.id === product.id);
    const existingQuantity = existingProduct ? existingProduct.quantity : 0;
  
    if (!product.excludeFromStock && product.stock < quantity + existingQuantity) {
      setNotification({ message: 'No hay suficiente stock disponible.', show: true });
      return;
    }
  
    const updatedCart = existingProduct
      ? cart.map(item => item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item)
      : [...cart, { ...product, quantity, collection: 'otherProducts' }]; // Specify 'products' collection here
  
    setCart(updatedCart);
    setNotification({ message: `Añadido ${quantity} ${product.model} al carrito.`, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 1000);
  };
  
  

  const handleAction = (action, product) => {
    if (action === 'edit' || action === 'delete') {
      if (!isAdmin) {
        navigate('/login');
        return;
      }
      if (!isEditMode) {
        setNotification({
          message: 'Activá el modo edición en la barra (botón 📝 / ✏️) para editar el catálogo.',
          show: true
        });
        setTimeout(() => setNotification({ message: '', show: false }), 4500);
        return;
      }
    }
    if (action === 'edit') {
      setEditProduct(product);
      setIsModalOpen(true);
    } else if (action === 'delete') {
      handleDelete(product);
    } else if (action === 'view') {
      setSelectedProduct(product);
    }
  };

  const handleDelete = async (product) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar este producto?")) {
      try {
        await deleteDoc(doc(db, 'otherProducts', product.id));
        setProducts(products.filter(p => p.id !== product.id));
        setNotification({ message: 'Producto eliminado con éxito.', show: true });
      } catch (error) {
        console.error("Error al eliminar el producto: ", error);
        setNotification({ message: 'Error al eliminar el producto.', show: true });
      } finally {
        setTimeout(() => setNotification({ message: '', show: false }), 3000);
      }
    }
  };

  const handleProductSubmit = async (productData) => {
    setLoading(true);
    try {
      if (editProduct) {
        await updateDoc(doc(db, 'otherProducts', editProduct.id), productData);
        setProducts(products.map(p => p.id === editProduct.id ? { ...productData, id: editProduct.id } : p));
        setNotification({ message: 'Producto actualizado con éxito.', show: true });
      } else {
        const docRef = await addDoc(collection(db, 'otherProducts'), productData);
        setProducts([...products, { ...productData, id: docRef.id }]);
        setNotification({ message: 'Producto agregado con éxito.', show: true });
      }
      setIsModalOpen(false);
      setEditProduct(null);
      fetchProducts();
    } catch (error) {
      console.error("Error al guardar el producto: ", error);
      setNotification({ message: 'Error al guardar el producto.', show: true });
    } finally {
      setLoading(false);
      setTimeout(() => setNotification({ message: '', show: false }), 3000);
    }
  };

  const filteredProducts = products.filter(product =>
    shouldShowCatalogProduct(product, canEditCatalog) &&
    matchesCatalogCategory(product, selectedCategory) &&
    (!showAvailableOnly || product.excludeFromStock || product.stock > 0)
  );
  const selectedCategoryLabel = selectedCategory === 'all' ? 'Todas las categorías' : selectedCategory;

  if (loading) return <Spinner />;

  const productFormModal = isModalOpen
    ? createPortal(
      <div className="modal-overlay-container">
        <div className="modal-content-container">
          <ProductForm
            product={editProduct}
            onSubmit={handleProductSubmit}
            onCancel={() => {
              setIsModalOpen(false);
              setEditProduct(null);
            }}
            categories={categories}
            setCategories={setCategories}
          />
        </div>
      </div>,
      document.body
    )
    : null;

  const productDetailModal = selectedProduct
    ? createPortal(
      <ProductDetailModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />,
      document.body
    )
    : null;

  return (
    <>
      <div className="cyber-shell cyber-shell--wide products-page">
        <div className="cyber-shell__frame" aria-hidden="true" />
        <div className="cyber-shell__body">
        <h2 className="cyber-shell__title">Consumo interno</h2>
      <div className="products-container">
        <Notification message={notification.message} show={notification.show} />
        <div className="category-buttons-container">
          <div className={`category-menu ${isCategoryMenuOpen ? 'is-open' : ''}`}>
            <button
              type="button"
              className="category-menu__trigger"
              onClick={() => setIsCategoryMenuOpen(prev => !prev)}
              aria-expanded={isCategoryMenuOpen}
              aria-haspopup="true"
            >
              <span className="category-menu__trigger-label">Categoría</span>
              <span className="category-menu__trigger-value">{selectedCategoryLabel}</span>
              <span className="category-menu__trigger-icon" aria-hidden="true">⌄</span>
            </button>
            <div className="category-menu__panel">
              <button
                type="button"
                onClick={() => {
                  setSelectedCategory('all');
                  setShowAvailableOnly(false);
                  setIsCategoryMenuOpen(false);
                }}
                className={`category-menu__option ${selectedCategory === 'all' ? 'is-selected' : ''}`}
              >
                Todas las categorías
              </button>
              {categories.map(category => (
                <button
                  key={category}
                  type="button"
                  onClick={() => {
                    setSelectedCategory(category);
                    setShowAvailableOnly(false);
                    setIsCategoryMenuOpen(false);
                  }}
                  className={`category-menu__option ${selectedCategory === category ? 'is-selected' : ''}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="ball-list-container">
          {filteredProducts.map((product) => (
            <ProductItem
              key={product.id}
              product={product}
              addToCart={addToCart}
              canEditCatalog={canEditCatalog}
              handleAction={handleAction}
            />
          ))}
        </div>
        {canEditCatalog && <button type="button" onClick={() => setIsModalOpen(true)} className="category-button cyber-btn">Agregar Producto</button>}
      </div>
        </div>
      </div>
      {productFormModal}
      {productDetailModal}
    </>
  );
};

const ProductItem = ({ product, addToCart, canEditCatalog, handleAction }) => {
  const [quantity, setQuantity] = useState(1);
  const countsStock = product.excludeFromStock !== true;
  const maxQuantity = countsStock ? Math.max(0, Number(product.stock) || 0) : Number.MAX_SAFE_INTEGER;
  const isOutOfStock = countsStock && maxQuantity <= 0;

  const updateQuantity = (nextQuantity) => {
    const parsedQuantity = parseInt(nextQuantity, 10);
    const safeQuantity = Number.isNaN(parsedQuantity) ? 1 : parsedQuantity;
    const clampedQuantity = Math.min(Math.max(safeQuantity, 1), Math.max(maxQuantity, 1));
    setQuantity(clampedQuantity);
  };

  return (
    <div className={`product-item-container ${isOutOfStock ? 'out-of-stock' : ''} ${product.hidden ? 'is-hidden-product' : ''}`}>
      {canEditCatalog && product.hidden && (
        <span className="product-hidden-badge">Oculto</span>
      )}
      <BallCard
        model={product.model}
        image={product.images ? product.images[0] : ''}
        price={`$${formatNumber(product.price)}`}
        category={product.category}
        onClick={() => handleAction('view', product)} 
      />
      <div className="product-actions-container">
        <div className="product-quantity-row">
          <label htmlFor={`quantity-${product.id}`} className="product-action-label">Cantidad</label>
          <div className="quantity-stepper">
            <button
              type="button"
              className="quantity-stepper-btn"
              onClick={() => updateQuantity(quantity - 1)}
              disabled={isOutOfStock || quantity <= 1}
              aria-label={`Bajar cantidad de ${product.model}`}
            >
              −
            </button>
            <div className="quantity-stepper-field">
              <input
                type="number"
                id={`quantity-${product.id}`}
                value={quantity}
                min={1}
                max={countsStock ? maxQuantity || 1 : undefined}
                onChange={(e) => updateQuantity(e.target.value)}
                className="quantity-input"
                disabled={isOutOfStock}
              />
            </div>
            <button
              type="button"
              className="quantity-stepper-btn"
              onClick={() => updateQuantity(quantity + 1)}
              disabled={isOutOfStock || quantity >= maxQuantity}
              aria-label={`Subir cantidad de ${product.model}`}
            >
              +
            </button>
          </div>
        </div>
        <button className="product-action-button add" onClick={() => addToCart(product, quantity)} disabled={isOutOfStock}>
          {!isOutOfStock ? 'Agregar al Carrito' : 'Sin Stock'}
        </button>
       
        {canEditCatalog && (
          <>
            <button type="button" className="product-action-button" onClick={() => handleAction('edit', product)}>Editar</button>
            <button type="button" className="product-action-button delete" onClick={() => handleAction('delete', product)}>Eliminar</button>
          </>
        )}
      </div>
    </div>
  );
};

const BallCard = ({ model, image, price, onClick }) => { 
  return (
    <div className="ball-card-container" onClick={onClick}> 
      <img src={image} alt={model} className="ball-card-image" />
      <h3 className="ball-card-title">{model}</h3>
      <p className="ball-card-price">{price}</p>
    </div>
  );
};


export default Products;
